import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import {
  ThemeProvider as MaterialThemeProvider,
  StyledEngineProvider,
  createTheme,
} from '@mui/material/styles';

const fontStack = '"Exo 2", "Roboto", "Helvetica", "Arial", sans-serif';

const Theme: React.FC<PropsWithChildren> = ({ children }) => {
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: 'light',
          primary: {
            main: '#0d6b7c',
            light: '#3d8f9e',
            dark: '#084854',
            contrastText: '#fff',
          },
          secondary: {
            main: '#5c6770',
            light: '#8a9399',
            dark: '#3e454b',
            contrastText: '#fff',
          },
          background: {
            default: '#f4f7f8',
            paper: '#ffffff',
          },
          divider: 'rgba(13, 107, 124, 0.12)',
        },
        typography: {
          fontFamily: fontStack,
          h1: { fontWeight: 600 },
          h2: { fontWeight: 600 },
          h3: { fontWeight: 600 },
          h4: { fontWeight: 600 },
          h5: { fontWeight: 600 },
          h6: { fontWeight: 600 },
          button: { textTransform: 'none', fontWeight: 600 },
        },
        shape: {
          borderRadius: 10,
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                fontFamily: fontStack,
              },
            },
          },
          MuiButton: {
            defaultProps: {
              disableElevation: true,
            },
            styleOverrides: {
              root: {
                borderRadius: 10,
              },
            },
          },
          MuiTextField: {
            defaultProps: {
              variant: 'outlined',
              size: 'small',
            },
          },
          MuiTabs: {
            styleOverrides: {
              root: {
                minHeight: 44,
              },
              indicator: {
                height: 3,
                borderRadius: 3,
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 44,
              },
            },
          },
          MuiCard: {
            styleOverrides: {
              root: {
                borderRadius: 12,
                boxShadow:
                  '0 1px 2px rgba(13, 107, 124, 0.06), 0 4px 16px rgba(13, 107, 124, 0.06)',
              },
            },
          },
        },
      }),
    [],
  );

  return (
    <StyledEngineProvider injectFirst>
      <MaterialThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MaterialThemeProvider>
    </StyledEngineProvider>
  );
};

export default Theme;
